// Executive Protection Detail Planner.
//
// The operational workspace for a single protective detail. A header shows the
// principal, status (with state-machine transitions, gated on detail.lead) and
// threat level; below it a tabbed builder covers:
//   - Timeline    : itinerary stops ordered by day/sequence/time
//   - Roster      : detail_assignments (agents + roles)
//   - Advances    : venue advances (advance.*)
//   - Itinerary   : stop create/edit (shared data with Timeline)
//   - Motorcade   : movement legs (primary/alternate routes, vehicles, ETAs)
//   - Threats     : threat_assessments editor (threat.*)
//
// Reads the detail id from the route (/details/:id). Each section is permission
// gated via can() from auth/store and uses the Dialog + DateInput shells.
//
// NOTE: route for this page (/details/:id) is wired in App.tsx during the
// Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  ProtectiveDetail,
  DetailStatus,
  ThreatLevel,
  DetailAssignment,
  DetailAssignmentRole,
  Advance,
  Itinerary,
  ItineraryStopType,
  MovementLeg,
  MovementLegStatus,
  ThreatAssessment,
  Personnel,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Table,
  Tabs,
  Textarea,
  type TabItem,
} from '../components/ui';
import {
  useDetail,
  useTransitionDetail,
  useDetailAgents,
  useCreateDetailAgent,
  useUpdateDetailAgent,
  useDeleteDetailAgent,
  useAdvances,
  useCreateAdvance,
  useUpdateAdvance,
  useDeleteAdvance,
  useItineraries,
  useCreateItinerary,
  useUpdateItinerary,
  useDeleteItinerary,
  useMovementLegs,
  useCreateMovementLeg,
  useUpdateMovementLeg,
  useDeleteMovementLeg,
  useThreatAssessments,
  useCreateThreatAssessment,
  useUpdateThreatAssessment,
  useDeleteThreatAssessment,
  usePrincipalsLookup,
  useOrganizationsLookup,
  usePersonnelLookup,
} from '../api/queries/details';
import {
  DETAIL_STATUSES,
  THREAT_LEVELS,
  StatusBadge,
  ThreatBadge,
  errMessage,
  fmtDate,
  fmtDateTime,
  principalName,
  organizationName,
} from './DetailsPage';

// ---------- Static option lists ----------

const ASSIGNMENT_ROLES: DetailAssignmentRole[] = [
  'detail_leader',
  'advance',
  'driver',
  'close_protection',
  'counter_surveillance',
  'medic',
];

const STOP_TYPES: ItineraryStopType[] = [
  'residence',
  'office',
  'event',
  'meal',
  'transit',
  'medical',
  'other',
];

const LEG_STATUSES: MovementLegStatus[] = ['planned', 'en_route', 'arrived', 'aborted'];

// Legal next-states, mirroring DETAIL_TRANSITIONS in routes/details.ts. The
// server is authoritative; this just keeps the UI from offering dead ends.
const DETAIL_TRANSITIONS: Record<DetailStatus, DetailStatus[]> = {
  planning: ['advance', 'active', 'standby', 'canceled'],
  advance: ['active', 'standby', 'canceled'],
  active: ['standby', 'completed', 'canceled'],
  standby: ['active', 'completed', 'canceled'],
  completed: [],
  canceled: [],
};

function personnelName(personnel: Personnel[], id: number): string {
  const p = personnel.find((x) => x.id === id);
  return p ? p.legal_name : `#${id}`;
}

function fmtRole(role: DetailAssignmentRole | null): string {
  return role ? role.replace(/_/g, ' ') : '—';
}

// =============================================================================
// Page
// =============================================================================

export function DetailPlannerPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const detailId = Number(params.id);
  const validId = Number.isInteger(detailId) && detailId > 0;

  const detailQuery = useDetail(validId ? detailId : null);
  const principalsLookup = usePrincipalsLookup();
  const orgsLookup = useOrganizationsLookup();

  const detail = detailQuery.data?.detail ?? null;
  const principals = principalsLookup.data?.principals ?? [];
  const organizations = orgsLookup.data?.organizations ?? [];

  if (!can('detail.read')) {
    return (
      <div className="detail-planner-page">
        <p className="error-text" role="alert">
          You don’t have permission to view this detail.
        </p>
      </div>
    );
  }

  if (!validId) {
    return (
      <div className="detail-planner-page">
        <p className="error-text" role="alert">Invalid detail id.</p>
      </div>
    );
  }

  const tabItems: TabItem[] = detail
    ? [
        { value: 'timeline', label: 'Timeline', content: <TimelineTab detailId={detailId} /> },
        { value: 'roster', label: 'Roster', content: <RosterTab detailId={detailId} /> },
        { value: 'advances', label: 'Advances', content: <AdvancesTab detailId={detailId} /> },
        { value: 'itinerary', label: 'Itinerary', content: <ItineraryTab detailId={detailId} /> },
        { value: 'motorcade', label: 'Motorcade', content: <MotorcadeTab detailId={detailId} /> },
        {
          value: 'threats',
          label: 'Threats',
          content: (
            <ThreatsTab detailId={detailId} fallbackPrincipalId={detail.principal_id} />
          ),
        },
      ]
    : [];

  return (
    <div className="detail-planner-page">
      <Button
        variant="ghost"
        className="btn-sm back-link"
        onClick={() => navigate('/details')}
      >
        ← All details
      </Button>

      {detailQuery.isLoading && <p className="muted">Loading detail…</p>}
      {detailQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(detailQuery.error, 'Failed to load detail.')}
        </p>
      )}

      {detail && (
        <>
          <DetailHeader
            detail={detail}
            principalLabel={principalName(principals, detail.principal_id)}
            orgLabel={organizationName(organizations, detail.organization_id)}
          />

          <Tabs items={tabItems} defaultValue="timeline" />
        </>
      )}
    </div>
  );
}

// =============================================================================
// Header — principal, status (with transitions) + threat level
// =============================================================================

function DetailHeader({
  detail,
  principalLabel,
  orgLabel,
}: {
  detail: ProtectiveDetail;
  principalLabel: string;
  orgLabel: string;
}): JSX.Element {
  const canLead = can('detail.lead');
  const transition = useTransitionDetail();
  const [confirmTarget, setConfirmTarget] = useState<DetailStatus | null>(null);

  const nextStates = DETAIL_TRANSITIONS[detail.status] ?? [];

  const onConfirmTransition = async (): Promise<void> => {
    if (!confirmTarget) return;
    try {
      await transition.mutateAsync({ id: detail.id, status: confirmTarget });
      toast.success(`Detail moved to “${confirmTarget}”.`);
      setConfirmTarget(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to change status.'));
    }
  };

  return (
    <header className="detail-header planner-header">
      <div>
        <h2>{principalLabel}</h2>
        <div className="detail-subline">
          <StatusBadge status={detail.status} />
          <ThreatBadge level={detail.threat_level} />
          <span className="muted">Org: {orgLabel}</span>
        </div>
        <div className="planner-window muted">
          {fmtDateTime(detail.start_at)} → {fmtDateTime(detail.end_at)}
        </div>
        {detail.notes && <p className="detail-description">{detail.notes}</p>}
      </div>

      {canLead && nextStates.length > 0 && (
        <div className="detail-actions">
          <span className="muted small">Move to:</span>
          {nextStates.map((s) => (
            <Button
              key={s}
              variant={s === 'canceled' ? 'danger' : 'ghost'}
              className="btn-sm"
              onClick={() => setConfirmTarget(s)}
              disabled={transition.isPending}
            >
              {s}
            </Button>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title="Change detail status"
        message={
          confirmTarget
            ? `Move this detail from “${detail.status}” to “${confirmTarget}”?`
            : ''
        }
        confirmLabel="Change status"
        danger={confirmTarget === 'canceled'}
        busy={transition.isPending}
        onConfirm={onConfirmTransition}
        onCancel={() => setConfirmTarget(null)}
      />
    </header>
  );
}

// =============================================================================
// Timeline tab — itinerary stops + movement legs in chronological order
// =============================================================================

function TimelineTab({ detailId }: { detailId: number }): JSX.Element {
  const itinQuery = useItineraries(detailId);
  const legsQuery = useMovementLegs(detailId);

  const stops = itinQuery.data?.itineraries ?? [];
  const legs = legsQuery.data?.movement_legs ?? [];

  // Group stops by day for a readable agenda. Stops with no day fall under
  // "Unscheduled". Within a day, the server already orders by sequence/time.
  const byDay = useMemo(() => {
    const groups = new Map<string, Itinerary[]>();
    for (const s of stops) {
      const key = s.day !== null ? fmtDate(s.day) : 'Unscheduled';
      const arr = groups.get(key) ?? [];
      arr.push(s);
      groups.set(key, arr);
    }
    return Array.from(groups.entries());
  }, [stops]);

  if (itinQuery.isLoading || legsQuery.isLoading) {
    return <p className="muted">Loading timeline…</p>;
  }

  if (stops.length === 0 && legs.length === 0) {
    return (
      <p className="muted empty-state">
        Nothing scheduled yet. Add stops on the Itinerary tab and routes on the
        Motorcade tab to build the timeline.
      </p>
    );
  }

  return (
    <div className="timeline">
      {byDay.map(([day, dayStops]) => (
        <section key={day} className="timeline-day">
          <h4 className="timeline-day-head">{day}</h4>
          <ol className="timeline-list">
            {dayStops.map((s) => (
              <li key={s.id} className="timeline-item">
                <span className="timeline-time">
                  {s.scheduled_at !== null
                    ? new Date(s.scheduled_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </span>
                <span className="timeline-body">
                  <strong>{s.location ?? 'Unnamed stop'}</strong>
                  {s.type && <Badge tone="neutral">{s.type}</Badge>}
                  {s.sequence !== null && (
                    <span className="muted small">seq {s.sequence}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ))}

      {legs.length > 0 && (
        <section className="timeline-day">
          <h4 className="timeline-day-head">Movement legs</h4>
          <ol className="timeline-list">
            {legs.map((l) => (
              <li key={l.id} className="timeline-item">
                <span className="timeline-time">
                  {l.est_depart !== null
                    ? new Date(l.est_depart).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </span>
                <span className="timeline-body">
                  <strong>
                    {(l.origin ?? '?')} → {(l.destination ?? '?')}
                  </strong>
                  <Badge tone="neutral">{l.status}</Badge>
                  {l.leg_no !== null && <span className="muted small">leg {l.leg_no}</span>}
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

// =============================================================================
// Roster tab — detail_assignments
// =============================================================================

function RosterTab({ detailId }: { detailId: number }): JSX.Element {
  const canWrite = can('detail.write');
  const agentsQuery = useDetailAgents(detailId);
  const personnelLookup = usePersonnelLookup();
  const deleteAgent = useDeleteDetailAgent();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; agent: DetailAssignment } | null
  >(null);
  const [deleting, setDeleting] = useState<DetailAssignment | null>(null);

  const agents = agentsQuery.data?.assignments ?? [];
  const personnel = personnelLookup.data?.personnel ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteAgent.mutateAsync({ detailId, agentId: deleting.id });
      toast.success('Removed from roster.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to remove agent.'));
    }
  };

  return (
    <div className="planner-section">
      <div className="detail-section-head">
        <h4>Roster</h4>
        {canWrite && (
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            Assign agent
          </Button>
        )}
      </div>

      {agentsQuery.isLoading && <p className="muted">Loading roster…</p>}
      {!agentsQuery.isLoading && agents.length === 0 && (
        <p className="muted empty-state">No agents assigned yet.</p>
      )}

      {agents.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Agent</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>From</Table.HeaderCell>
              <Table.HeaderCell>To</Table.HeaderCell>
              <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {agents.map((a) => (
              <Table.Row key={a.id}>
                <Table.Cell tone="strong">{personnelName(personnel, a.personnel_id)}</Table.Cell>
                <Table.Cell>{fmtRole(a.role)}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(a.starts_at)}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(a.ends_at)}</Table.Cell>
                <Table.Cell className="col-actions">
                  {canWrite && (
                    <>
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => setModal({ mode: 'edit', agent: a })}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => setDeleting(a)}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {modal && (
        <RosterFormModal
          detailId={detailId}
          personnel={personnel}
          agent={modal.mode === 'edit' ? modal.agent : undefined}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Remove agent"
        message={
          deleting
            ? `Remove ${personnelName(personnel, deleting.personnel_id)} from this detail?`
            : ''
        }
        confirmLabel="Remove"
        danger
        busy={deleteAgent.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function RosterFormModal({
  detailId,
  personnel,
  agent,
  onClose,
}: {
  detailId: number;
  personnel: Personnel[];
  agent?: DetailAssignment;
  onClose: () => void;
}): JSX.Element {
  const isEdit = agent !== undefined;
  const create = useCreateDetailAgent();
  const update = useUpdateDetailAgent();
  const busy = create.isPending || update.isPending;

  const [personnelId, setPersonnelId] = useState<number | ''>(agent?.personnel_id ?? '');
  const [role, setRole] = useState<DetailAssignmentRole | ''>(agent?.role ?? '');
  const [startsAt, setStartsAt] = useState<number | null>(agent?.starts_at ?? null);
  const [endsAt, setEndsAt] = useState<number | null>(agent?.ends_at ?? null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (personnelId === '') {
      toast.warn('Select an agent.');
      return;
    }
    try {
      if (isEdit && agent) {
        await update.mutateAsync({
          detailId,
          agentId: agent.id,
          personnel_id: personnelId,
          role: role === '' ? null : role,
          starts_at: startsAt,
          ends_at: endsAt,
        });
        toast.success('Assignment updated.');
      } else {
        await create.mutateAsync({
          detailId,
          personnel_id: personnelId,
          role: role === '' ? null : role,
          starts_at: startsAt,
          ends_at: endsAt,
        });
        toast.success('Agent assigned.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save assignment.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit assignment' : 'Assign agent'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="roster-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Assign'}
          </Button>
        </>
      }
    >
      <form id="roster-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Agent">
          <select
            className="form-input"
            value={personnelId}
            onChange={(e) => setPersonnelId(e.target.value ? Number(e.target.value) : '')}
            required
            autoFocus
          >
            <option value="">Select personnel…</option>
            {personnel.map((p) => (
              <option key={p.id} value={p.id}>
                {p.legal_name}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Role">
          <select
            className="form-input"
            value={role}
            onChange={(e) => setRole(e.target.value as DetailAssignmentRole | '')}
          >
            <option value="">Unspecified</option>
            {ASSIGNMENT_ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="On shift from">
          <DateInput value={startsAt} onChange={setStartsAt} withTime />
        </FormField>

        <FormField label="Until">
          <DateInput value={endsAt} onChange={setEndsAt} withTime minEpoch={startsAt ?? undefined} />
        </FormField>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Advances tab
// =============================================================================

function AdvancesTab({ detailId }: { detailId: number }): JSX.Element {
  const canRead = can('advance.read');
  const canWrite = can('advance.write');
  const advancesQuery = useAdvances(detailId);
  const deleteAdvance = useDeleteAdvance();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; advance: Advance } | null
  >(null);
  const [deleting, setDeleting] = useState<Advance | null>(null);

  const advances = advancesQuery.data?.advances ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteAdvance.mutateAsync({ detailId, advanceId: deleting.id });
      toast.success('Advance deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete advance.'));
    }
  };

  if (!canRead) {
    return <p className="muted">You don’t have permission to view advances.</p>;
  }

  return (
    <div className="planner-section">
      <div className="detail-section-head">
        <h4>Advances</h4>
        {canWrite && (
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            New advance
          </Button>
        )}
      </div>

      {advancesQuery.isLoading && <p className="muted">Loading advances…</p>}
      {!advancesQuery.isLoading && advances.length === 0 && (
        <p className="muted empty-state">No venue advances recorded yet.</p>
      )}

      <div className="card-stack">
        {advances.map((a) => (
          <article key={a.id} className="advance-card">
            <header className="advance-card-head">
              <strong>{a.venue ?? 'Unnamed venue'}</strong>
              {(a.lat !== null || a.lng !== null) && (
                <span className="muted small">
                  {a.lat ?? '?'}, {a.lng ?? '?'}
                </span>
              )}
              {canWrite && (
                <span className="card-actions">
                  <Button
                    variant="ghost"
                    className="btn-sm"
                    onClick={() => setModal({ mode: 'edit', advance: a })}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => setDeleting(a)}
                  >
                    Delete
                  </Button>
                </span>
              )}
            </header>
            <dl className="advance-card-body">
              {a.safe_room && (
                <>
                  <dt>Safe room</dt>
                  <dd>{a.safe_room}</dd>
                </>
              )}
              {a.egress_notes && (
                <>
                  <dt>Egress</dt>
                  <dd>{a.egress_notes}</dd>
                </>
              )}
            </dl>
          </article>
        ))}
      </div>

      {modal && (
        <AdvanceFormModal
          detailId={detailId}
          advance={modal.mode === 'edit' ? modal.advance : undefined}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete advance"
        message={deleting ? `Delete the advance for “${deleting.venue ?? 'this venue'}”?` : ''}
        confirmLabel="Delete"
        danger
        busy={deleteAdvance.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function AdvanceFormModal({
  detailId,
  advance,
  onClose,
}: {
  detailId: number;
  advance?: Advance;
  onClose: () => void;
}): JSX.Element {
  const isEdit = advance !== undefined;
  const create = useCreateAdvance();
  const update = useUpdateAdvance();
  const busy = create.isPending || update.isPending;

  const [venue, setVenue] = useState(advance?.venue ?? '');
  const [lat, setLat] = useState(advance?.lat ?? '');
  const [lng, setLng] = useState(advance?.lng ?? '');
  const [safeRoom, setSafeRoom] = useState(advance?.safe_room ?? '');
  const [egress, setEgress] = useState(advance?.egress_notes ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const payload = {
      venue: venue.trim() ? venue.trim() : null,
      lat: lat.trim() ? lat.trim() : null,
      lng: lng.trim() ? lng.trim() : null,
      safe_room: safeRoom.trim() ? safeRoom.trim() : null,
      egress_notes: egress.trim() ? egress.trim() : null,
    };
    try {
      if (isEdit && advance) {
        await update.mutateAsync({ detailId, advanceId: advance.id, ...payload });
        toast.success('Advance updated.');
      } else {
        await create.mutateAsync({ detailId, ...payload });
        toast.success('Advance created.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save advance.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit advance' : 'New advance'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="advance-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="advance-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Venue">
          <Input
            type="text"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            maxLength={500}
            autoFocus
          />
        </FormField>

        <FormRow>
          <FormField label="Latitude">
            <Input
              type="text"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="e.g. 34.052235"
            />
          </FormField>
          <FormField label="Longitude">
            <Input
              type="text"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="e.g. -118.243683"
            />
          </FormField>
        </FormRow>

        <FormField label="Safe room">
          <Input
            type="text"
            value={safeRoom}
            onChange={(e) => setSafeRoom(e.target.value)}
            maxLength={2000}
            placeholder="Location of designated safe room."
          />
        </FormField>

        <FormField label="Egress notes">
          <Textarea
            value={egress}
            onChange={(e) => setEgress(e.target.value)}
            rows={4}
            maxLength={10_000}
            placeholder="Primary and alternate egress routes, choke points, staging."
          />
        </FormField>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Itinerary tab — stop create/edit
// =============================================================================

function ItineraryTab({ detailId }: { detailId: number }): JSX.Element {
  const canWrite = can('detail.write');
  const itinQuery = useItineraries(detailId);
  const deleteStop = useDeleteItinerary();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; stop: Itinerary } | null
  >(null);
  const [deleting, setDeleting] = useState<Itinerary | null>(null);

  const stops = itinQuery.data?.itineraries ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteStop.mutateAsync({ detailId, itinId: deleting.id });
      toast.success('Stop deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete stop.'));
    }
  };

  return (
    <div className="planner-section">
      <div className="detail-section-head">
        <h4>Itinerary builder</h4>
        {canWrite && (
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            Add stop
          </Button>
        )}
      </div>

      {itinQuery.isLoading && <p className="muted">Loading itinerary…</p>}
      {!itinQuery.isLoading && stops.length === 0 && (
        <p className="muted empty-state">No itinerary stops yet.</p>
      )}

      {stops.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Seq</Table.HeaderCell>
              <Table.HeaderCell>Day</Table.HeaderCell>
              <Table.HeaderCell>Time</Table.HeaderCell>
              <Table.HeaderCell>Location</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {stops.map((s) => (
              <Table.Row key={s.id}>
                <Table.Cell>{s.sequence ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">{fmtDate(s.day)}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(s.scheduled_at)}</Table.Cell>
                <Table.Cell tone="strong">{s.location ?? '—'}</Table.Cell>
                <Table.Cell>{s.type ? <Badge tone="neutral">{s.type}</Badge> : '—'}</Table.Cell>
                <Table.Cell className="col-actions">
                  {canWrite && (
                    <>
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => setModal({ mode: 'edit', stop: s })}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => setDeleting(s)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {modal && (
        <ItineraryFormModal
          detailId={detailId}
          stop={modal.mode === 'edit' ? modal.stop : undefined}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete stop"
        message={deleting ? `Delete “${deleting.location ?? 'this stop'}” from the itinerary?` : ''}
        confirmLabel="Delete"
        danger
        busy={deleteStop.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function ItineraryFormModal({
  detailId,
  stop,
  onClose,
}: {
  detailId: number;
  stop?: Itinerary;
  onClose: () => void;
}): JSX.Element {
  const isEdit = stop !== undefined;
  const create = useCreateItinerary();
  const update = useUpdateItinerary();
  const busy = create.isPending || update.isPending;

  const [day, setDay] = useState<number | null>(stop?.day ?? null);
  const [sequence, setSequence] = useState<string>(
    stop?.sequence !== null && stop?.sequence !== undefined ? String(stop.sequence) : '',
  );
  const [location, setLocation] = useState(stop?.location ?? '');
  const [lat, setLat] = useState(stop?.lat ?? '');
  const [lng, setLng] = useState(stop?.lng ?? '');
  const [scheduledAt, setScheduledAt] = useState<number | null>(stop?.scheduled_at ?? null);
  const [type, setType] = useState<ItineraryStopType | ''>(stop?.type ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const seqNum = sequence.trim() === '' ? null : Number(sequence);
    if (seqNum !== null && (!Number.isInteger(seqNum) || seqNum < 0)) {
      toast.warn('Sequence must be a non-negative whole number.');
      return;
    }
    const payload = {
      day,
      sequence: seqNum,
      location: location.trim() ? location.trim() : null,
      lat: lat.trim() ? lat.trim() : null,
      lng: lng.trim() ? lng.trim() : null,
      scheduled_at: scheduledAt,
      type: type === '' ? null : type,
    };
    try {
      if (isEdit && stop) {
        await update.mutateAsync({ detailId, itinId: stop.id, ...payload });
        toast.success('Stop updated.');
      } else {
        await create.mutateAsync({ detailId, ...payload });
        toast.success('Stop added.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save stop.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit stop' : 'Add itinerary stop'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="itinerary-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="itinerary-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Location">
          <Input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={500}
            autoFocus
          />
        </FormField>

        <FormRow>
          <FormField label="Day">
            <DateInput value={day} onChange={setDay} />
          </FormField>
          <FormField label="Scheduled">
            <DateInput value={scheduledAt} onChange={setScheduledAt} withTime />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Sequence">
            <Input
              type="number"
              min={0}
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              placeholder="Order within the day"
            />
          </FormField>
          <FormField label="Type">
            <select
              className="form-input"
              value={type}
              onChange={(e) => setType(e.target.value as ItineraryStopType | '')}
            >
              <option value="">Unspecified</option>
              {STOP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Latitude">
            <Input
              type="text"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="e.g. 34.052235"
            />
          </FormField>
          <FormField label="Longitude">
            <Input
              type="text"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="e.g. -118.243683"
            />
          </FormField>
        </FormRow>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Motorcade tab — movement legs
// =============================================================================

function MotorcadeTab({ detailId }: { detailId: number }): JSX.Element {
  const canWrite = can('detail.write');
  const legsQuery = useMovementLegs(detailId);
  const deleteLeg = useDeleteMovementLeg();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; leg: MovementLeg } | null
  >(null);
  const [deleting, setDeleting] = useState<MovementLeg | null>(null);

  const legs = legsQuery.data?.movement_legs ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteLeg.mutateAsync({ detailId, legId: deleting.id });
      toast.success('Leg deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete leg.'));
    }
  };

  return (
    <div className="planner-section">
      <div className="detail-section-head">
        <h4>Motorcade legs</h4>
        {canWrite && (
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            Add leg
          </Button>
        )}
      </div>

      {legsQuery.isLoading && <p className="muted">Loading legs…</p>}
      {!legsQuery.isLoading && legs.length === 0 && (
        <p className="muted empty-state">No movement legs planned yet.</p>
      )}

      {legs.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Leg</Table.HeaderCell>
              <Table.HeaderCell>Origin</Table.HeaderCell>
              <Table.HeaderCell>Destination</Table.HeaderCell>
              <Table.HeaderCell>Depart</Table.HeaderCell>
              <Table.HeaderCell>Arrive</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {legs.map((l) => (
              <Table.Row key={l.id}>
                <Table.Cell>{l.leg_no ?? '—'}</Table.Cell>
                <Table.Cell tone="strong">{l.origin ?? '—'}</Table.Cell>
                <Table.Cell tone="strong">{l.destination ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(l.est_depart)}</Table.Cell>
                <Table.Cell tone="muted">{fmtDateTime(l.est_arrive)}</Table.Cell>
                <Table.Cell>
                  <Badge tone="neutral">{l.status}</Badge>
                </Table.Cell>
                <Table.Cell className="col-actions">
                  {canWrite && (
                    <>
                      <Button
                        variant="ghost"
                        className="btn-sm"
                        onClick={() => setModal({ mode: 'edit', leg: l })}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm"
                        onClick={() => setDeleting(l)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {modal && (
        <MotorcadeFormModal
          detailId={detailId}
          leg={modal.mode === 'edit' ? modal.leg : undefined}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete leg"
        message={
          deleting
            ? `Delete leg ${deleting.leg_no ?? ''} (${deleting.origin ?? '?'} → ${deleting.destination ?? '?'})?`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteLeg.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function MotorcadeFormModal({
  detailId,
  leg,
  onClose,
}: {
  detailId: number;
  leg?: MovementLeg;
  onClose: () => void;
}): JSX.Element {
  const isEdit = leg !== undefined;
  const create = useCreateMovementLeg();
  const update = useUpdateMovementLeg();
  const busy = create.isPending || update.isPending;

  const [legNo, setLegNo] = useState<string>(
    leg?.leg_no !== null && leg?.leg_no !== undefined ? String(leg.leg_no) : '',
  );
  const [origin, setOrigin] = useState(leg?.origin ?? '');
  const [destination, setDestination] = useState(leg?.destination ?? '');
  const [primaryRoute, setPrimaryRoute] = useState(leg?.primary_route ?? '');
  const [alternateRoute, setAlternateRoute] = useState(leg?.alternate_route ?? '');
  const [estDepart, setEstDepart] = useState<number | null>(leg?.est_depart ?? null);
  const [estArrive, setEstArrive] = useState<number | null>(leg?.est_arrive ?? null);
  const [status, setStatus] = useState<MovementLegStatus>(leg?.status ?? 'planned');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const legNoNum = legNo.trim() === '' ? null : Number(legNo);
    if (legNoNum !== null && (!Number.isInteger(legNoNum) || legNoNum < 0)) {
      toast.warn('Leg number must be a non-negative whole number.');
      return;
    }
    const payload = {
      leg_no: legNoNum,
      origin: origin.trim() ? origin.trim() : null,
      destination: destination.trim() ? destination.trim() : null,
      primary_route: primaryRoute.trim() ? primaryRoute.trim() : null,
      alternate_route: alternateRoute.trim() ? alternateRoute.trim() : null,
      est_depart: estDepart,
      est_arrive: estArrive,
      status,
    };
    try {
      if (isEdit && leg) {
        await update.mutateAsync({ detailId, legId: leg.id, ...payload });
        toast.success('Leg updated.');
      } else {
        await create.mutateAsync({ detailId, ...payload });
        toast.success('Leg added.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save leg.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit leg' : 'Add motorcade leg'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="leg-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Add'}
          </Button>
        </>
      }
    >
      <form id="leg-form" onSubmit={onSubmit} className="form-grid">
        <FormRow>
          <FormField label="Leg number">
            <Input
              type="number"
              min={0}
              value={legNo}
              onChange={(e) => setLegNo(e.target.value)}
            />
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as MovementLegStatus)}
            >
              {LEG_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Origin">
            <Input
              type="text"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              maxLength={500}
              autoFocus
            />
          </FormField>
          <FormField label="Destination">
            <Input
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              maxLength={500}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Est. depart">
            <DateInput value={estDepart} onChange={setEstDepart} withTime />
          </FormField>
          <FormField label="Est. arrive">
            <DateInput
              value={estArrive}
              onChange={setEstArrive}
              withTime
              minEpoch={estDepart ?? undefined}
            />
          </FormField>
        </FormRow>

        <FormField label="Primary route">
          <Textarea
            value={primaryRoute}
            onChange={(e) => setPrimaryRoute(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Turn-by-turn primary route."
          />
        </FormField>

        <FormField label="Alternate route">
          <Textarea
            value={alternateRoute}
            onChange={(e) => setAlternateRoute(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Contingency / alternate route."
          />
        </FormField>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Threats tab — threat_assessments editor
// =============================================================================

function ThreatsTab({
  detailId,
  fallbackPrincipalId,
}: {
  detailId: number;
  fallbackPrincipalId: number;
}): JSX.Element {
  const canRead = can('threat.read');
  const canWrite = can('threat.write');
  const threatsQuery = useThreatAssessments(detailId);
  const deleteThreat = useDeleteThreatAssessment();

  const [modal, setModal] = useState<
    { mode: 'create' } | { mode: 'edit'; threat: ThreatAssessment } | null
  >(null);
  const [deleting, setDeleting] = useState<ThreatAssessment | null>(null);

  const threats = threatsQuery.data?.threat_assessments ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteThreat.mutateAsync({ detailId, taId: deleting.id });
      toast.success('Threat assessment deleted.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete assessment.'));
    }
  };

  if (!canRead) {
    return <p className="muted">You don’t have permission to view threat assessments.</p>;
  }

  return (
    <div className="planner-section">
      <div className="detail-section-head">
        <h4>Threat assessments</h4>
        {canWrite && (
          <Button
            variant="ghost"
            className="btn-sm"
            onClick={() => setModal({ mode: 'create' })}
          >
            New assessment
          </Button>
        )}
      </div>

      {threatsQuery.isLoading && <p className="muted">Loading assessments…</p>}
      {!threatsQuery.isLoading && threats.length === 0 && (
        <p className="muted empty-state">No threat assessments recorded yet.</p>
      )}

      <div className="card-stack">
        {threats.map((t) => (
          <article key={t.id} className="threat-card">
            <header className="threat-card-head">
              <ThreatBadge level={t.level} />
              <span className="muted small">Assessed {fmtDateTime(t.assessed_at)}</span>
              {canWrite && (
                <span className="card-actions">
                  <Button
                    variant="ghost"
                    className="btn-sm"
                    onClick={() => setModal({ mode: 'edit', threat: t })}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => setDeleting(t)}
                  >
                    Delete
                  </Button>
                </span>
              )}
            </header>
            {Array.isArray(t.indicators) && t.indicators.length > 0 && (
              <ul className="threat-indicators">
                {t.indicators.map((ind, i) => (
                  <li key={i}>{typeof ind === 'string' ? ind : JSON.stringify(ind)}</li>
                ))}
              </ul>
            )}
            {t.mitigations && <p className="threat-mitigations">{t.mitigations}</p>}
          </article>
        ))}
      </div>

      {modal && (
        <ThreatFormModal
          detailId={detailId}
          fallbackPrincipalId={fallbackPrincipalId}
          threat={modal.mode === 'edit' ? modal.threat : undefined}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete assessment"
        message="Delete this threat assessment?"
        confirmLabel="Delete"
        danger
        busy={deleteThreat.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function ThreatFormModal({
  detailId,
  fallbackPrincipalId,
  threat,
  onClose,
}: {
  detailId: number;
  fallbackPrincipalId: number;
  threat?: ThreatAssessment;
  onClose: () => void;
}): JSX.Element {
  const isEdit = threat !== undefined;
  const create = useCreateThreatAssessment();
  const update = useUpdateThreatAssessment();
  const busy = create.isPending || update.isPending;

  const [level, setLevel] = useState<ThreatLevel>(threat?.level ?? 'low');
  // Indicators are a JSONB array of strings in the editor — one per line.
  const [indicatorsText, setIndicatorsText] = useState(() =>
    Array.isArray(threat?.indicators)
      ? threat!.indicators
          .map((i) => (typeof i === 'string' ? i : JSON.stringify(i)))
          .join('\n')
      : '',
  );
  const [mitigations, setMitigations] = useState(threat?.mitigations ?? '');
  const [assessedAt, setAssessedAt] = useState<number | null>(threat?.assessed_at ?? null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const indicators = indicatorsText
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const payload = {
      level,
      indicators,
      mitigations: mitigations.trim() ? mitigations.trim() : null,
      assessed_at: assessedAt,
    };
    try {
      if (isEdit && threat) {
        await update.mutateAsync({ detailId, taId: threat.id, ...payload });
        toast.success('Assessment updated.');
      } else {
        await create.mutateAsync({
          detailId,
          principal_id: fallbackPrincipalId,
          ...payload,
        });
        toast.success('Assessment created.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save assessment.'));
    }
  };

  return (
    <Dialog
      title={isEdit ? 'Edit threat assessment' : 'New threat assessment'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="threat-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="threat-form" onSubmit={onSubmit} className="form-grid">
        <FormRow>
          <FormField label="Level">
            <select
              className="form-input"
              value={level}
              onChange={(e) => setLevel(e.target.value as ThreatLevel)}
            >
              {THREAT_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Assessed at">
            <DateInput value={assessedAt} onChange={setAssessedAt} withTime />
          </FormField>
        </FormRow>

        <FormField label="Indicators (one per line)">
          <Textarea
            value={indicatorsText}
            onChange={(e) => setIndicatorsText(e.target.value)}
            rows={5}
            placeholder={'Hostile surveillance noted\nSocial-media doxxing\n…'}
          />
        </FormField>

        <FormField label="Mitigations">
          <Textarea
            value={mitigations}
            onChange={(e) => setMitigations(e.target.value)}
            rows={5}
            maxLength={20_000}
            placeholder="Countermeasures and recommended posture."
          />
        </FormField>
      </form>
    </Dialog>
  );
}
